import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { createAbortController, handleVirtualScrollData } from "@coronet/cui";
import type { Pagination } from "@/types";
import { AxiosError } from "axios";
import type { WorkspaceType } from "@/constants/workplaces";
import { WorkspaceLocale } from "@/constants/workplaces";
import cloneDeep from "lodash/cloneDeep";
import { StoreModule } from "@/constants/store.ts";
import type { Subscription } from "@/_store/msp/msp.module.ts";
import { GlobalPolicySection } from "@/constants/msp.ts";

let abortController: AbortController | undefined;

interface BetaComponent {
  type: string;
  name: string;
  endDate: number;
}

export interface WorkspaceListItem {
  workspaceId: string;
  name: string;
  type: WorkspaceType;
  mfaRequired: boolean;
}

interface GlobalPolicyInfo {
  mspPolicyId: string;
  workspaceId: string;
  name: string;
  sections: GlobalPolicySection[];
}

export interface WorkspaceDetails {
  workspaceId: string;
  allowPolicyOverriding: boolean;
  mspPolicyShortInfo: GlobalPolicyInfo;
  name: string;
  domain: string;
  type: WorkspaceType;
  mfaRequired: boolean;
  scanUnprotectedUsers: boolean;
  supportEnabled: boolean;
  branding: {
    headerIconUrl?: string;
    supportEmail?: string;
    displayName?: string;
    brandColor?: string;
  };
  isCoronetWorkspace: boolean;
  subscription: Subscription;
  subscriptionType: string;
  trialEndDate?: number;
  headerIconUrl?: string;
  showDisabledModules: boolean;
  betaComponents: Array<BetaComponent>;
  languageCode: WorkspaceLocale;
  parentWorkspaceName?: string;
  demoModeEnabled?: boolean;
  demoModeEndDate?: number | null;
  trialModeStartedNotice?: boolean;
  protectedUsers?: number;
  protectedUsersLimit?: number;
}

interface WorkspacesState {
  workspaces: Array<WorkspaceListItem>;
  total: number;
  pagination: Pagination;
  pendingInvites: Array<WorkspaceListItem>;
  pendingInvitesTotal: number;
  pendingInvitesPagination: Pagination;
  loading: boolean;
}

const defaultWorkspaceState = {
  workspaces: [],
  total: 1,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  pendingInvites: [],
  pendingInvitesTotal: 0,
  pendingInvitesPagination: {
    page: 0,
    pageSize: 25,
  },
  loading: true,
};

export const useWorkspacesListStore = defineStore(StoreModule.WORKSPACES_LIST, {
  state: (): WorkspacesState => cloneDeep(defaultWorkspaceState),
  persist: false,
  actions: {
    async getWorkspaces(name: string = "", virtuallyScrollable = false) {
      abortController = createAbortController(abortController);
      this.loading = true;
      const filters = {
        name: name,
        ...this.pagination,
      };

      const request = {
        ...api.workplaces(filters),
      };

      try {
        const { data } = await axiosInstance.request({
          ...request,
          signal: abortController.signal,
        });
        this.workspaces = virtuallyScrollable
          ? handleVirtualScrollData(this.workspaces, data.items, "workspaceId")
          : data.items;
        this.total = data.total;
        this.loading = false;
      } catch (err) {
        if (err && (err as AxiosError).code === AxiosError.ERR_CANCELED) {
          return;
        }
        console.error(err);
        this.loading = false;
      }
      abortController = undefined;
    },
    async getPendingInvites(name: string = "", virtuallyScrollable = false) {
      this.loading = true;
      const request = {
        ...api.pendingInvitesToWorkplace(name, this.pendingInvitesPagination),
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.pendingInvites = virtuallyScrollable
          ? handleVirtualScrollData(this.pendingInvites, data.items, "workspaceId")
          : data.items;
        this.pendingInvitesTotal = data.total;
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    },
    joinWorkplace(id: string) {
      const request = {
        ...api.joinWorkplace(id),
      };
      return axiosInstance.request(request);
    },
    setWorkspaces(workspaces: WorkspaceListItem[]) {
      this.workspaces = workspaces;
    },
    setPendingInvites(workspaces: WorkspaceListItem[]) {
      this.pendingInvites = workspaces;
    },
    setPagination(paging: Pagination) {
      this.pagination = paging;
    },
    setPendingInvitesPagination(paging: Pagination) {
      this.pendingInvitesPagination = paging;
    },
    resetState() {
      this.$reset();
    },
  },
});
